import { getDownloadUrl } from '@/contexts/media/actions/getDownloadUrl';
import { logger } from '@/shared/infrastructure/logging/logger';

/**
 * Get download URL with Content-Disposition: attachment header
 * Uses server action to generate SAS URL server-side
 */
async function getMediaDownloadUrl(
  fileUrl: string,
  filename?: string
): Promise<string> {
  try {
    const downloadUrl = await getDownloadUrl(fileUrl, filename);
    if (downloadUrl) return downloadUrl;

    logger.warn('[downloadUrl] No download URL returned, using original');
    return fileUrl;
  } catch (error) {
    logger.error('[downloadUrl] Failed to get download URL:', error);
    return fileUrl;
  }
}

/**
 * Trigger file download by navigating to URL.
 *
 * Setting `link.download = filename` is what tells the browser to treat
 * the anchor click as a download rather than a navigation. For same-origin
 * URLs the filename is honored; for cross-origin URLs (e.g. Azure SAS) the
 * browser ignores the attribute value but still respects the DOWNLOAD
 * INTENT when paired with a server-side `Content-Disposition: attachment`.
 * Omitting this attribute — as the previous implementation did — caused
 * cross-origin SAS URLs to open a new tab instead of downloading.
 *
 * In Telegram Mini App, uses openLink() to open in external browser
 * (prevents white screen from in-app redirect interception).
 */
function triggerDownload(url: string, filename?: string): void {
  const tg = (window as any).Telegram?.WebApp;
  if (tg) {
    // Bot API 8.0+ — native download sheet with filename, keeps user in-app.
    // Older clients stub `downloadFile` as a throwing method AND log the
    // "not supported" error before the throw lands in our catch, so we
    // check the reported version first to keep the console clean.
    const versionOk = parseFloat(tg.version ?? '0') >= 8;
    if (versionOk && typeof tg.downloadFile === 'function') {
      try {
        tg.downloadFile({ url, file_name: filename });
        return;
      } catch {
        // fall through to openLink
      }
    }
    if (typeof tg.openLink === 'function') {
      // Older clients: open SAS in external browser (Content-Disposition:
      // attachment on the SAS makes the browser save instead of navigate).
      tg.openLink(url);
      return;
    }
  }

  const link = document.createElement('a');
  link.href = url;
  if (filename) link.download = filename;
  // Never replace the CURRENT page: for a cross-origin URL the browser ignores
  // `download` and would navigate the current tab (opening the PDF/inline in
  // place). `target=_blank` keeps the user on their page — when the server sends
  // Content-Disposition: attachment it downloads with no visible tab; otherwise
  // it opens a throwaway tab instead of navigating away.
  link.target = '_blank';
  link.rel = 'noopener';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Download file using server-action-generated URL
 */
export async function downloadFile(fileUrl: string, filename: string): Promise<void> {
  const downloadUrl = await getMediaDownloadUrl(fileUrl, filename);
  triggerDownload(downloadUrl, filename);
}
